import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Paper, Snackbar, Tab, Tabs, Typography } from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LabelsService from "../../../services/LabelsService";
import LabelsTab from "./LabelsTab";
import LabelGroupsTab from "./LabelGroupsTab";
import { BRAND_COLOR, getErrorMessage } from "./labelUtils";

const LabelGroupsManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [labels, setLabels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const notify = useCallback((text, severity = "success") => setToast({ text, severity }), []);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [labelsResponse, groupsResponse] = await Promise.all([
        LabelsService.getLabelsForManagement(),
        LabelsService.getLabelGroupsOverview(),
      ]);
      setLabels(labelsResponse || []);
      setGroups(groupsResponse || []);
    } catch (error) {
      notify(getErrorMessage(error, "No se pudo cargar la información de etiquetas."), "error");
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Gestor de etiquetas
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Consulta, edita y elimina las etiquetas de la plataforma y organízalas en grupos. Los cambios se
          reflejan automáticamente en cursos, publicaciones, contenidos y usuarios.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{ mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider", px: 1 }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 15, minHeight: 56 },
            "& .Mui-selected": { color: `${BRAND_COLOR} !important` },
            "& .MuiTabs-indicator": { backgroundColor: BRAND_COLOR, height: 3, borderRadius: 3 },
          }}
        >
          <Tab
            icon={<LocalOfferOutlinedIcon />}
            iconPosition="start"
            label={`Etiquetas (${labels.length})`}
          />
          <Tab
            icon={<FolderOutlinedIcon />}
            iconPosition="start"
            label={`Grupos de etiquetas (${groups.length})`}
          />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <LabelsTab labels={labels} isLoading={isLoading} onChanged={loadData} onNotify={notify} />
      )}
      {activeTab === 1 && (
        <LabelGroupsTab
          groups={groups}
          labels={labels}
          isLoading={isLoading}
          onChanged={loadData}
          onNotify={notify}
        />
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
};

export default LabelGroupsManager;
